(function () {
  'use strict';

  var cartBar = document.getElementById('quick-cart-bar');
  var lastCart = null;

  /* Per-variant state for the +/- stepper's debounced/serialized commits.
     Keyed by variant id: { target, inFlight, dirty, timer }. While an entry
     exists for a variant, that variant's control is "locally owned" — a
     generic cart sync (e.g. from another control's add-to-cart, or the
     initial page-load refresh) must not overwrite its displayed quantity
     with a possibly-stale server value. */
  var stepState = {};

  function formatMoney(cents) {
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  /* ============================================================
     "Added to cart" toast
     Built on first use and reused. Shown the instant ADD is
     tapped, hidden after three seconds.
     ============================================================ */
  var toastEl = null;
  var toastTimer = null;
  var toastHideTimer = null;

  function buildToast() {
    if (toastEl) return toastEl;

    var style = document.createElement('style');
    style.textContent =
      '.zt-toast{position:fixed;left:12px;right:12px;z-index:120;display:flex;align-items:center;gap:10px;' +
      'padding:9px 10px 9px 12px;border-radius:14px;background:var(--navy,#1B2A4A);color:#fff;' +
      'box-shadow:0 12px 28px rgba(27,42,74,.32);font-size:.8rem;font-weight:700;' +
      'opacity:0;transform:translateY(12px);transition:opacity .2s ease,transform .2s ease;}' +
      '.zt-toast.is-on{opacity:1;transform:translateY(0);}' +
      '.zt-toast[hidden]{display:none;}' +
      '.zt-toast__tick{flex:0 0 20px;width:20px;height:20px;border-radius:50%;background:#46C46B;' +
      'color:#08301A;display:flex;align-items:center;justify-content:center;font-size:.66rem;font-weight:900;}' +
      '.zt-toast__text{flex:1 1 auto;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
      '.zt-toast__link{flex:0 0 auto;background:var(--marigold,#F2A93E);color:var(--navy,#1B2A4A);' +
      'text-decoration:none;font-weight:800;font-size:.76rem;padding:7px 13px;border-radius:999px;white-space:nowrap;}' +
      '@media (min-width:860px){.zt-toast{left:auto;right:20px;width:320px;}}' +
      '@media (prefers-reduced-motion:reduce){.zt-toast{transition:none;}}';
    document.head.appendChild(style);

    toastEl = document.createElement('div');
    toastEl.className = 'zt-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.hidden = true;
    toastEl.innerHTML =
      '<span class="zt-toast__tick" aria-hidden="true">&#10003;</span>' +
      '<span class="zt-toast__text">Added to cart</span>' +
      '<a class="zt-toast__link" href="/cart">View cart</a>';
    document.body.appendChild(toastEl);
    return toastEl;
  }

  /* Sit above whatever is already pinned to the bottom — the tab bar, the
     quick cart bar, the live order strip — rather than guessing an offset. */
  function toastBottom() {
    var lowest = 12;
    ['.bnav', '#quick-cart-bar', '[data-ostrip]'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el || el.hidden) return;
      var r = el.getBoundingClientRect();
      if (!r.height) return;
      var fromBottom = window.innerHeight - r.top + 10;
      if (fromBottom > lowest) lowest = fromBottom;
    });
    return lowest;
  }

  function showAdded() {
    var el = buildToast();
    clearTimeout(toastTimer);
    clearTimeout(toastHideTimer);
    el.style.bottom = toastBottom() + 'px';
    el.hidden = false;
    void el.offsetWidth; /* reflow, so the transition runs on a freshly shown element */
    el.classList.add('is-on');
    toastTimer = setTimeout(hideAdded, 3000);
  }

  function hideAdded() {
    if (!toastEl) return;
    clearTimeout(toastTimer);
    toastEl.classList.remove('is-on');
    toastHideTimer = setTimeout(function () {
      if (toastEl) toastEl.hidden = true;
    }, 220);
  }

  function variantIdIn(el) {
    if (!el) return null;
    var id = el.getAttribute && el.getAttribute('data-variant-id');
    if (!id) {
      var form = el.closest && el.closest('form');
      var input = form && (form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]'));
      if (input) id = input.value;
    }
    return id ? String(id) : null;
  }

  function renderControl(ctrl, n) {
    var addBtn = ctrl.querySelector('[data-add]');
    var stepper = ctrl.querySelector('[data-stepper]');
    if (!addBtn) return;
    if (!stepper) { addBtn.hidden = false; return; }
    if (n > 0) {
      addBtn.hidden = true;
      stepper.hidden = false;
      var qtyEl = stepper.querySelector('[data-qty]');
      if (qtyEl) qtyEl.textContent = n;
    } else {
      addBtn.hidden = false;
      stepper.hidden = true;
    }
  }

  function syncControls(cart) {
    var qty = {};
    (cart.items || []).forEach(function (item) {
      var k = String(item.variant_id);
      qty[k] = (qty[k] || 0) + item.quantity;
    });
    var controls = document.querySelectorAll('[data-cart-control]');
    Array.prototype.forEach.call(controls, function (ctrl) {
      var id = variantIdIn(ctrl);
      if (id && stepState[id]) return; /* a stepper commit for this variant is still pending/in-flight — don't stomp it with a possibly out-of-date cart snapshot */
      renderControl(ctrl, id ? (qty[id] || 0) : 0);
    });
  }

  function updateCart(cart) {
    lastCart = cart;
    if (cartBar) {
      if (cart.item_count > 0) {
        cartBar.hidden = false;
        var countEl = cartBar.querySelector('.quick-cart-bar__count');
        var priceEl = cartBar.querySelector('.quick-cart-bar__price');
        if (countEl) countEl.textContent = cart.item_count;
        if (priceEl) priceEl.textContent = formatMoney(cart.total_price);
      } else {
        cartBar.hidden = true;
      }
    }

    /* The header badge is always in the markup now, hidden at zero, so it
       can be filled in without a page reload the first time something is
       added to an empty cart. */
    var headerCount = document.querySelector('.site-header__cart-count');
    if (headerCount) {
      headerCount.textContent = cart.item_count > 99 ? '99+' : cart.item_count;
      headerCount.hidden = !(cart.item_count > 0);
    }

    /* The bottom tab bar's badge listens for this rather than watching
       network traffic, so both badges move together. */
    window.__zetseCartCount = cart.item_count;
    try {
      document.dispatchEvent(new CustomEvent('zetse:cart', { detail: { item_count: cart.item_count } }));
    } catch (e) {}

    syncControls(cart);
  }

  function refreshCart() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(updateCart);
  }

  /* Count the item in straight away, before the request goes out, so the
     badges and the stepper all move on the tap. The response only trues up
     the money afterwards. */
  function patchCartAddOptimistic(variantId) {
    if (!lastCart) return false;
    var items = lastCart.items || (lastCart.items = []);
    var existing = items.filter(function (li) { return String(li.variant_id) === String(variantId); })[0];
    if (existing) {
      existing.quantity += 1;
    } else {
      items.push({ variant_id: variantId, quantity: 1 });
    }
    lastCart.item_count = (lastCart.item_count || 0) + 1;
    return true;
  }

  /* Patch the cached cart locally to a target quantity for the +/- stepper, mirroring
     the add path, so the stepper can update instantly instead of waiting on the server. */
  function patchCartQuantity(variantId, quantity) {
    if (!lastCart) return false;
    var items = lastCart.items || (lastCart.items = []);
    var idx = -1;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === String(variantId)) { idx = i; break; }
    }
    if (idx === -1) return false;
    var item = items[idx];
    var unitPrice = item.price || (item.final_line_price / (item.quantity || 1)) || 0;
    var diff = quantity - item.quantity;
    item.quantity = quantity;
    item.line_price = unitPrice * quantity;
    item.final_line_price = item.line_price;
    lastCart.item_count = (lastCart.item_count || 0) + diff;
    lastCart.total_price = (lastCart.total_price || 0) + unitPrice * diff;
    if (quantity <= 0) items.splice(idx, 1);
    return true;
  }

  /* +/- clicks are debounced per variant: the UI updates immediately (optimistic),
     but the actual /cart/change.js request only fires ~350ms after the last tap.
     Mashing +/- a few times in a row feels instant and sends one network call
     instead of one per click. Requests are also SERIALIZED per variant — only
     one /cart/change.js call is ever in flight for a given variant at a time.
     Without that, two overlapping requests (e.g. quantity 3 then quantity 5)
     can resolve out of order on a slow connection, and whichever response
     lands last wins — which is exactly what made the quantity look like it
     was randomly increasing/decreasing on its own instead of settling on the
     number you last tapped to. */
  function sendCommit(variantId) {
    var state = stepState[variantId];
    if (!state) return;
    if (state.inFlight) { state.dirty = true; return; }
    state.inFlight = true;
    state.dirty = false;
    var target = state.target;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: target })
    })
      .then(function (r) {
        /* A non-2xx response (e.g. 422 when the requested quantity exceeds
           available stock) still has a JSON body, but it's an error object,
           not a cart — {"items": [...]} vs {"status":422,"message":"..."}.
           Treating that error object as the cart (the previous bug here)
           wiped out cart.items, which is what made the quantity on screen
           reset to 0 out of nowhere. Route non-ok responses to the catch
           below instead, same as the add-to-cart handler already does. */
        if (!r.ok) return r.json().then(function (err) { return Promise.reject(err); });
        return r.json();
      })
      .then(function (cart) {
        state.inFlight = false;
        if (state.dirty || state.target !== target) {
          /* The quantity changed again while this request was in flight —
             this response is already stale, so don't apply it. Immediately
             fire the request for the latest target instead. */
          sendCommit(variantId);
        } else {
          delete stepState[variantId];
          updateCart(cart);
        }
      })
      .catch(function () {
        state.inFlight = false;
        delete stepState[variantId];
        /* True up with the real cart state (e.g. the request was rejected
           because it exceeded available stock). */
        refreshCart();
      });
  }

  document.addEventListener('click', function (e) {
    var stepBtn = e.target.closest('[data-step]');
    if (stepBtn) {
      e.preventDefault();
      var stepCtrl = stepBtn.closest('[data-cart-control]');
      if (!stepCtrl) return;
      var stepId = variantIdIn(stepCtrl);
      if (!stepId) return;
      var qtyEl = stepCtrl.querySelector('[data-qty]');
      var current = qtyEl ? (parseInt(qtyEl.textContent, 10) || 0) : 0;
      var next = current + (parseInt(stepBtn.getAttribute('data-step'), 10) || 0);
      if (next < 0) next = 0;

      var state = stepState[stepId];
      if (!state) state = stepState[stepId] = { target: next, inFlight: false, dirty: false, timer: null };
      state.target = next;
      if (state.inFlight) state.dirty = true;

      /* Optimistic UI: reflect the new quantity (and totals) the instant the
         button is tapped, instead of waiting on the network round trip. */
      renderControl(stepCtrl, next);
      if (patchCartQuantity(stepId, next)) updateCart(lastCart);

      clearTimeout(state.timer);
      state.timer = setTimeout(function () { sendCommit(stepId); }, 350);
      return;
    }

    var addBtn = e.target.closest('.p-card__add, .product-detail__add');
    if (!addBtn || addBtn.disabled) return;
    var ctrl = addBtn.closest('[data-cart-control]');
    var variantId = variantIdIn(ctrl) || variantIdIn(addBtn);
    if (!variantId) return;
    e.preventDefault();

    /* Optimistic UI: flip straight to the quantity stepper, count the item
       into both badges and show the toast the instant the button is tapped,
       instead of waiting on the network. The fetch below still runs to
       confirm with the server and true up totals. */
    addBtn.disabled = true;
    if (ctrl) renderControl(ctrl, 1);
    if (patchCartAddOptimistic(variantId)) {
      updateCart(lastCart);
    }
    showAdded();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { return Promise.reject(err); });
        return r.json();
      })
      .then(function (item) {
        addBtn.disabled = false;
        if (!lastCart) { refreshCart(); return; }
        /* The quantity was already counted on the tap — only the money is
           outstanding. final_price is the unit price of the line. */
        var unit = (item.final_price != null ? item.final_price : item.price) || 0;
        lastCart.total_price = (lastCart.total_price || 0) + unit;
        updateCart(lastCart);
      })
      .catch(function () {
        addBtn.disabled = false;
        /* Take the optimistic count and the toast back, then true up with
           the real cart (e.g. it went out of stock since page load). */
        hideAdded();
        refreshCart();
      });
  });

  document.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'id' && lastCart) syncControls(lastCart);
  });

  refreshCart();
})();
(function () {
  'use strict';
  var form = document.getElementById('HeaderSearchForm');
  var input = document.getElementById('HeaderSearchInput');
  var panel = document.getElementById('HeaderSearchResults');
  if (!form || !input || !panel) return;
  var MAX_PRODUCTS = 10, MAX_COLLECTIONS = 5, debounceTimer = null, activeQuery = '', lastFocus = null;
  var back = document.createElement('button');
  back.type = 'button'; back.className = 'search-predictive__back'; back.setAttribute('aria-label', 'Close search');
  back.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  form.insertBefore(back, form.firstChild);
  function esc(str) { return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) { return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]; }); }
  function money(amount) { return '₹' + Math.round(parseFloat(amount || 0)).toLocaleString('en-IN'); }
  function img(image) { return !image ? '' : (typeof image === 'string' ? image : image.url || image.src || ''); }
  function openSearch() { if (!document.body.classList.contains('search-predictive-open')) lastFocus = document.activeElement; document.body.classList.add('search-predictive-open'); input.setAttribute('aria-expanded','true'); panel.hidden = false; }
  function closeSearch() { document.body.classList.remove('search-predictive-open'); input.setAttribute('aria-expanded','false'); panel.hidden = true; if (lastFocus && lastFocus !== input && lastFocus.focus) lastFocus.focus(); }
  function renderStart() { panel.innerHTML = '<div class="search-predictive__welcome"><strong>Search Zetse</strong><span>Find books, stationery and school supplies</span></div>'; }
  function renderLoading() { panel.innerHTML = '<div class="search-predictive__loading">Searching…</div>'; }
  function renderEmpty(query) { panel.innerHTML = '<div class="search-predictive__empty"><strong>No matches for “' + esc(query) + '”</strong><span>Try a product, brand or collection name.</span></div>'; }
  function renderResults(query, collections, products) {
    if (!collections.length && !products.length) { renderEmpty(query); return; }
    var html = '';
    if (collections.length) {
      html += '<section class="search-predictive__section"><h2>Collections</h2><div class="search-predictive__collections">';
      collections.slice(0, MAX_COLLECTIONS).forEach(function (c, index) { var fallbackProduct = products[index % Math.max(products.length, 1)] || {}; var src = img(c.image || c.featured_image) || img(fallbackProduct.image || fallbackProduct.featured_image); html += '<a class="search-predictive__collection" href="' + esc(c.url) + '"><span class="search-predictive__collection-thumb">' + (src ? '<img src="' + esc(src) + '" alt="" loading="lazy">' : '<span aria-hidden="true">⌕</span>') + '</span><span>' + esc(c.title) + '</span></a>'; });
      html += '</div></section>';
    }
    if (products.length) {
      html += '<section class="search-predictive__section search-predictive__products-section"><div class="search-predictive__section-head"><h2>Products</h2><a href="/search?type=product&q=' + encodeURIComponent(query) + '">View all</a></div><div class="search-predictive__products">';
      products.slice(0, MAX_PRODUCTS).forEach(function (p) { var src = img(p.image || p.featured_image), compare = p.compare_at_price_max || p.compare_at_price, hasCompare = compare && parseFloat(compare) > parseFloat(p.price); html += '<a class="search-predictive__product" href="' + esc(p.url) + '"><span class="search-predictive__product-img">' + (src ? '<img src="' + esc(src) + '" alt="" loading="lazy">' : '') + '</span><span class="search-predictive__product-title">' + esc(p.title) + '</span><span class="search-predictive__product-price">' + money(p.price) + (hasCompare ? '<s>' + money(compare) + '</s>' : '') + '</span></a>'; });
      html += '</div></section>';
    }
    panel.innerHTML = html;
  }
  function addProductImages(products) {
    if (!products.length || products.some(function (p) { return img(p.image || p.featured_image); })) return Promise.resolve(products);
    return fetch('/products.json?limit=250').then(function (r) { return r.json(); }).then(function (data) {
      var byHandle = {};
      (data.products || []).forEach(function (p) { byHandle[p.handle] = p.images && p.images[0] ? p.images[0].src : ''; });
      products.forEach(function (p) { var handle = String(p.url || '').split('/products/')[1]; if (handle) handle = handle.split('?')[0]; if (!img(p.image || p.featured_image) && byHandle[handle]) p.image = byHandle[handle]; });
      return products;
    }).catch(function () { return products; });
  }
  function runSearch(query) {
    activeQuery = query; renderLoading();
    fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product,collection&resources[limit]=10&resources[options][fields]=title,vendor,product_type,tag')
      .then(function (r) { if (!r.ok) throw new Error('Search failed'); return r.json(); })
      .then(function (data) {
        if (activeQuery !== query) return;
        var results = (data.resources && data.resources.results) || {};
        var products = results.products || [];
        return addProductImages(products).then(function (withImages) { if (activeQuery === query) renderResults(query, results.collections || [], withImages); });
      })
      .catch(function () { if (activeQuery === query) renderEmpty(query); });
  }

  input.addEventListener('focus', function () { openSearch(); if (!input.value.trim()) renderStart(); });
  input.addEventListener('input', function () { var query = input.value.trim(); openSearch(); clearTimeout(debounceTimer); if (!query) { activeQuery = ''; renderStart(); return; } debounceTimer = setTimeout(function () { runSearch(query); }, 180); });
  back.addEventListener('click', closeSearch);
  input.addEventListener('keydown', function (e) { if (e.key === 'Escape') { e.preventDefault(); closeSearch(); input.blur(); } });
  form.addEventListener('submit', function (e) { if (!input.value.trim()) e.preventDefault(); });
})();
